/* Supply-chain world map renderer — lifted verbatim from the prototype's
 * `WorldMap` object in smartwatch-sim.html (the animated <canvas> version) and
 * wrapped here as a framework-free module, per the porting pattern (handoff §5:
 * "reuse prototype renderers by extracting them verbatim and wrapping in React",
 * the same move as Watch3D → watch3d.ts). The React lifecycle lives in
 * SupplyMapView.tsx; this file is pure drawing.
 *
 * Decoupled from the prototype globals: instead of reading `Engine`, `ACTIVE()`
 * and module-scope game state directly, the caller passes a small `MapData`
 * bundle (resolved suppliers, the active country list, the markets map and a
 * `qDemand` fn). Behaviour is otherwise identical to the prototype. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { cssAlpha, cssColor } from '../lib/cssColor';

export interface SupplierPoint { id: string; name: string; lat: number; lon: number }
export interface CountryPoint { id: string; name: string; lat: number; lon: number }
export interface MapData {
  /** chosen component suppliers (cyan), in draw order */
  suppliers: SupplierPoint[];
  /** chosen final-assembly supplier (amber) */
  assembly: SupplierPoint;
  /** active country markets (violet dots; glow when a paid channel is open) */
  countries: CountryPoint[];
  /** per-country market record: paid when dist/sub/carrier is truthy */
  markets: Record<string, { dist?: any; sub?: any; carrier?: any } | undefined>;
  /** quarterly demand for a country, used to pick the top routed markets */
  qDemand: (c: CountryPoint) => number;
}

/* Real-world land mask: 66 rows (lat 74 → -56, step 2°) × 180 cols (lon -179 →
   179, step 2°). Each row is a 45-char hex string; bit i (LSB-first) = land at
   lon -179+2i. Copied verbatim from the prototype. */
const MASK = ["000010000ffe00060000000007fffc0000c00f0000000","0001f07fffffab030000000007fffc01be73fb8000000","083ffffffffffb08001e800007ffe807d0cff8405fe00","ffffffffffffffff93ffd000003ff82c9be38ffffff83","fffffffffffffffffc7ff000700ff04e0ffffffffff9e","7ffffffffffffffffefcf8003803f03c1bffffffffc00","3ffffffffffffffffffe7f040001e003807ffffffff80","0187ffffffffffffffe07e0000000027807fffff8ff40","00600ffffffffffffffc300c0000007f80fffffc02800","007007fffffffffffffe14080000007f43fffff800400","00300bfffffffffffffff426000001ff9fffffe800000","00103fffffffffffffffff7a000003ffbfffffc000000","00002fffffffffffffffff800000021fffffff4000000","00000ffffffffffffffffff000000f1fffffff0000000","000007fffffffffffe1fffe0000000bfffffff0000000","000063fffffffffffc0fcbe000000027ffffff0000000","0000007ffffffffff88f14fc00000007ffffff0000000","0000103bfffffffffff9043c00000001ffffff0000000","00000023fffffffffff2303c00000000ffffff0000000","00000c63ffffffffff0007c000000000fffffe0000000","00000103ffffffffff0007f8000000007ffff80000000","00000087ffffffffff833ffe000000003ffff00000000","00000007ffffffffbffffffe0000000019ffd00000000","00000003ffffffff3f7fffff00000000201fe00000000","00000003fffffff87f7fffff80000000201f800000000","00000005ffffff07feffffffc0000000001f000000000","000000007ff7fe0ffcffffffc0000000401e000000000","0000000007e1fc07fdffffffc0000001823e000000000","0000000407e0f803f9ffffffc000001083f8000000000","000000040fa07800fbffffffc000000003a0000000000","0000000c1f80380037ffffffc00000800f00000000000","000000101e9030000fffffffc00000000800000000000","0000000a008030007fffffff8000007f0000000000000","00000019008040007fffffff000000ffa000000000000","00000015010000003fffff7e000001ff8000000000000","00000001834000003ffff80000000fff8000000000000","00000001c38000001ffff80000001fff8000000000000","00000001e300000007fff80000000fffc000000000000","00008904e300000007fff8000000ffffc000000000000","00003f088600000003fff0000007ffffe000000000000","000bf8000800000001fff000000fffffe000000000000","0030b8006000000003ffe000001fffffc000000000000","000300000000000003ffe000000fffff8000000000000","000021000000000003ffe0000007ffff8000000000000","000023a00000000043fff0000007ffff0000000000000","000067f00000000063fff0000007fffe0000000000000","00007ff80000000071fff0000003fff80000000000000","0000fffc00000000307fe0000003fff80000000000000","0001ffff8000000030ffe0000003fff80000000000000","0003ffff8000000030ffe00000007ff80000000000000","0007ffff80000000003fe00000003ff80000000000000","0007ffff80000000003fc00000003ffc0000000000000","0007ffff80000000001f800000001ffc0000000000000","0003ff3f00000000001f800000000ffc0000000000000","0003f8038000000000038000000007fc0000000000000","0001f4000000000000000000000001fc0000000000000","6000b0000000000000000000000001fe0000000000000","0000000000000000000000000000007e0000000000000","1000800000000000000000000000001c0000000000000","0c00000000000000000000000000001e0000000000000","0600000000000000000000000000000f0000000000000","0000000000000000000000000000001f0000000000000","0000000000000000000000000000000e0000000000000","0000000000000000000000000000010f0000000000000","000000000000000000000000000000080000000000000","000000000000000000000000000000000000000000000"];

let _ptsCache: [number, number][] | null = null;
function dots(): [number, number][] {
  if (_ptsCache) return _ptsCache;
  const pts: [number, number][] = [];
  for (let r = 0; r < MASK.length; r++) {
    const lat = 74 - r * 2;
    const v = BigInt('0x' + MASK[r]);
    for (let i = 0; i < 180; i++) {
      if ((v >> BigInt(i)) & 1n) pts.push([-179 + i * 2, lat]);
    }
  }
  _ptsCache = pts;
  return pts;
}

function project(lon: number, lat: number, W: number, H: number): [number, number] {
  return [((lon + 180) / 360) * W, ((90 - lat) / 180) * H * 1.18 - H * 0.06];
}

function route(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, color: string, t: number) {
  const mx = (x1 + x2) / 2, my = Math.min(y1, y2) - Math.abs(x2 - x1) * 0.18 - 14;
  ctx.strokeStyle = color; ctx.beginPath(); ctx.moveTo(x1, y1); ctx.quadraticCurveTo(mx, my, x2, y2); ctx.stroke();
  // moving particle
  const tt = (t * 0.35 + (x1 * 7 + y1) % 10 / 10) % 1;
  const px = (1 - tt) * (1 - tt) * x1 + 2 * (1 - tt) * tt * mx + tt * tt * x2;
  const py = (1 - tt) * (1 - tt) * y1 + 2 * (1 - tt) * tt * my + tt * tt * y2;
  ctx.beginPath(); ctx.arc(px, py, 2.4, 0, 7); ctx.fillStyle = color.replace(/[\d.]+\)$/, '1)'); ctx.fill();
}

/* Colors come from the canvas's own computed style, so the map follows the
 * active theme without a second copy of the palette in JS. cssColor caches per
 * theme, which matters here — draw() runs every animation frame. */
function palette(el: HTMLCanvasElement) {
  return {
    land: cssAlpha(el, 'dim', 0.5),
    supplyRoute: cssAlpha(el, 'acc', 0.4),
    marketRoute: cssAlpha(el, 'acc2', 0.45),
    marketGlow: cssAlpha(el, 'acc2', 0.16),
    marketOn: cssColor(el, 'acc2'),
    marketOff: cssAlpha(el, 'acc2', 0.35),
    label: cssColor(el, 'txt'),
    supplierGlow: cssAlpha(el, 'acc', 0.15),
    supplier: cssColor(el, 'acc'),
    assemblyGlow: cssAlpha(el, 'warn', 0.2),
    assembly: cssColor(el, 'warn'),
  };
}

function draw(canvas: HTMLCanvasElement, d: MapData) {
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (!ctx) return;
  const P = palette(canvas);
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  // land dots
  ctx.fillStyle = P.land;
  for (const [lon, lat] of dots()) {
    const [x, y] = project(lon, lat, W, H);
    ctx.beginPath(); ctx.arc(x, y, 1.7, 0, 7); ctx.fill();
  }
  const t = Date.now() / 1000;
  const asm = d.assembly;
  const [ax, ay] = project(asm.lon, asm.lat, W, H);
  // routes: suppliers -> assembly
  ctx.lineWidth = 1.4;
  for (const s of d.suppliers) {
    if (s.id === asm.id) continue;
    const [sx, sy] = project(s.lon, s.lat, W, H);
    route(ctx, sx, sy, ax, ay, P.supplyRoute, t);
  }
  // markets: every country sells online by default (dim dots); paid distribution glows violet
  const paid = d.countries.filter((c) => { const m = d.markets[c.id]; return m && (m.dist || m.sub || m.carrier); });
  // routes: assembly -> top paid markets (by market size)
  const routed = paid.slice().sort((a, b) => d.qDemand(b) - d.qDemand(a)).slice(0, 8);
  for (const c of routed) {
    const [rx, ry] = project(c.lon, c.lat, W, H);
    route(ctx, ax, ay, rx, ry, P.marketRoute, t);
  }
  for (const c of d.countries) {
    const [x, y] = project(c.lon, c.lat, W, H);
    const m = d.markets[c.id]; const on = !!(m && (m.dist || m.sub || m.carrier));
    const rad = on ? 5 : 2.5;
    if (on) { ctx.beginPath(); ctx.arc(x, y, rad + 4 + Math.sin(t * 2 + x) * 1.2, 0, 7); ctx.fillStyle = P.marketGlow; ctx.fill(); }
    ctx.beginPath(); ctx.arc(x, y, rad, 0, 7);
    ctx.fillStyle = on ? P.marketOn : P.marketOff;
    ctx.fill();
    if (on && m!.sub) { ctx.lineWidth = 1.4; ctx.strokeStyle = P.label; ctx.beginPath(); ctx.arc(x, y, rad + 2.5, 0, 7); ctx.stroke(); }
  }
  for (const c of routed) {
    const [x, y] = project(c.lon, c.lat, W, H);
    ctx.fillStyle = P.label; ctx.font = '600 10px Segoe UI'; ctx.textAlign = 'center';
    ctx.fillText(c.name, x, y + 16);
  }
  // supplier markers
  for (const s of d.suppliers) {
    const [x, y] = project(s.lon, s.lat, W, H);
    const pulse = 4 + Math.sin(t * 2.4 + x) * 1.2;
    ctx.beginPath(); ctx.arc(x, y, pulse + 4, 0, 7); ctx.fillStyle = P.supplierGlow; ctx.fill();
    ctx.beginPath(); ctx.arc(x, y, 4, 0, 7); ctx.fillStyle = P.supplier; ctx.fill();
  }
  // assembly marker
  ctx.beginPath(); ctx.arc(ax, ay, 7 + Math.sin(t * 2.4) * 1.5, 0, 7); ctx.fillStyle = P.assemblyGlow; ctx.fill();
  ctx.beginPath(); ctx.arc(ax, ay, 5.5, 0, 7); ctx.fillStyle = P.assembly; ctx.fill();
  ctx.fillStyle = P.assembly; ctx.font = '700 10px Segoe UI'; ctx.textAlign = 'center'; ctx.fillText('ASSEMBLY · ' + asm.name, ax, ay - 12);
}

/** Start the animation loop; returns a stop fn that cancels it. */
export function animateSupplyMap(canvas: HTMLCanvasElement, getData: () => MapData): () => void {
  let raf = 0;
  const loop = () => {
    if (!document.body.contains(canvas)) { cancelAnimationFrame(raf); return; }
    draw(canvas, getData());
    raf = requestAnimationFrame(loop);
  };
  loop();
  return () => cancelAnimationFrame(raf);
}

/** Draw a single static frame (no rAF loop) — used under reduce-motion. */
export function drawSupplyMapStatic(canvas: HTMLCanvasElement, data: MapData): void {
  draw(canvas, data);
}
